// src/componentes/Apartados/ApartadosPorVencerBanner.jsx
//
// Banner para Dashboard: apartados activos con fecha de vencimiento <= hoy+7d.
// Se oculta si no hay nada por vencer.
// Permisos: requiere "deudas.ver".

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaBookmark, FaExclamationTriangle, FaArrowRight } from "react-icons/fa";
import { listarApartados, marcarVencidos } from "../../services/apartadosService";
import { useUserContext } from "../GestionDeUsuarios/UserContext";
import "./ApartadosPorVencerBanner.css";

function diasRestantes(value) {
  if (!value) return null;
  try {
    const d = value?.toDate ? value.toDate() : new Date(value);
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    return Math.round((d - hoy) / (1000 * 60 * 60 * 24));
  } catch { return null; }
}

export default function ApartadosPorVencerBanner({ dias = 7 }) {
  const navigate = useNavigate();
  const { can } = useUserContext();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!can("deudas.ver")) {
      setLoading(false);
      return;
    }
    let alive = true;
    (async () => {
      try {
        // Liberar primero los ya vencidos en silencio
        await marcarVencidos();
      } catch {}
      try {
        const lista = await listarApartados({ estado: "activo" });
        if (!alive) return;
        // Filtrar a los que vencen dentro de N días
        const proximos = lista.filter((a) => {
          const d = diasRestantes(a.fechaVencimiento);
          return d !== null && d >= 0 && d <= dias;
        });
        // Orden: los que vencen antes primero
        proximos.sort((a, b) => {
          const da = diasRestantes(a.fechaVencimiento) ?? 999;
          const dbb = diasRestantes(b.fechaVencimiento) ?? 999;
          return da - dbb;
        });
        setItems(proximos);
      } catch (err) {
        console.warn("ApartadosPorVencerBanner:", err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [can, dias]);

  if (loading || items.length === 0) return null;

  return (
    <section className="apartados-banner">
      <header className="apartados-banner-head">
        <div>
          <FaExclamationTriangle className="apartados-banner-icon" />
          <strong>
            {items.length} apartado{items.length === 1 ? "" : "s"} por vencer
          </strong>
          <span> en los próximos {dias} días</span>
        </div>
        <button
          className="apartados-banner-cta"
          onClick={() => navigate("/app/apartados")}
        >
          Ver todos <FaArrowRight />
        </button>
      </header>

      <ul className="apartados-banner-list">
        {items.slice(0, 5).map((a) => {
          const d = diasRestantes(a.fechaVencimiento);
          const venceHoy = d === 0;
          return (
            <li
              key={a.id}
              className={`apartados-banner-item ${venceHoy ? "vence-hoy" : ""}`}
              onClick={() =>
                navigate(`/app/cliente/${a.clienteId}/deudas/nueva?apartadoId=${a.id}`)
              }
              title="Click para convertir a deuda"
            >
              <FaBookmark />
              <div className="apartados-banner-item-info">
                <strong>{a.clienteNombre || "Sin cliente"}</strong>
                <small>
                  {a.propiedades?.[0]?.folio || "Propiedad"}
                  {a.propiedades?.length > 1 && ` +${a.propiedades.length - 1}`}
                </small>
              </div>
              <span className={`apartados-banner-dias ${venceHoy ? "hoy" : ""}`}>
                {venceHoy ? "HOY" : `${d}d`}
              </span>
            </li>
          );
        })}
        {items.length > 5 && (
          <li
            className="apartados-banner-more"
            onClick={() => navigate("/app/apartados")}
          >
            +{items.length - 5} más →
          </li>
        )}
      </ul>
    </section>
  );
}
